import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../components/NavigationBar';
import { GuidedSetupModal, SiteConfigTab } from '../components/guidedSetup/GuidedSetupModal';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import apiService from '../services/api';
import { Notification } from '../types';
import { hasActiveBusinessRoles, isBusinessOwnerForBusiness } from '../utils/roleUtils';
import {
  ArrowLeft,
  BookOpen,
  Briefcase,
  Building2,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ClipboardCheck,
  Globe2,
  Info,
  LifeBuoy,
  Mail,
  MessageSquare,
  Search,
  Users,
  Wand2,
  X
} from 'lucide-react';

type ChecklistItemId = 'timezone' | 'hours' | 'services' | 'bizCategory' | 'logo' | 'images' | 'theme';
type ChecklistItem = { id: ChecklistItemId; label: string; checked: boolean };

type SupportForm = {
  topic: string;
  steps: string;
  device: string;
};

type ModalShellProps = {
  icon: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
};

// IMPORTANT: Keep this component outside `HelpAndSupportPage` to avoid remounting
// the entire modal subtree (inputs losing focus on every keystroke).
const ModalShell: React.FC<ModalShellProps> = ({ icon, title, description, onClose, children, footer }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/50" onClick={onClose} />
    <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-start gap-4 w-full">
          <div className="h-11 w-11 rounded-2xl bg-gray-100 border border-gray-200/60 flex items-center justify-center shrink-0">
            {icon}
          </div>
          <div className="flex-grow min-w-0">
            <h3 className="text-xl font-semibold text-gray-900 mb-1">{title}</h3>
            {description ? <div className="text-sm text-gray-600">{description}</div> : null}
          </div>
          <button
            onClick={onClose}
            className="shrink-0 rounded-md p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="p-6 overflow-y-auto flex-1">{children}</div>

      {footer ? <div className="p-6 border-t border-gray-200">{footer}</div> : null}
    </div>
  </div>
);

const HelpAndSupportPage: React.FC = () => {
  const { currentBusiness } = useBusiness();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);

  const [showGuidedSetup, setShowGuidedSetup] = useState(false);
  const [guidedSetupStepIndex, setGuidedSetupStepIndex] = useState(0);

  const [showSetupChecklist, setShowSetupChecklist] = useState(false);
  const [showPageDefinitions, setShowPageDefinitions] = useState(false);
  const [showQA, setShowQA] = useState(false);
  const [showContactSupport, setShowContactSupport] = useState(false);

  const [expandedQA, setExpandedQA] = useState<string | null>('q1');
  const [qaSearch, setQaSearch] = useState('');
  const qaSearchInputRef = useRef<HTMLInputElement | null>(null);

  const [isLoadingSetupChecklist, setIsLoadingSetupChecklist] = useState(false);
  const [setupChecklistError, setSetupChecklistError] = useState<string | null>(null);

  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([
    { id: 'timezone', label: 'Set Timezone', checked: false },
    { id: 'hours', label: 'Set Business Hours', checked: false },
    { id: 'bizCategory', label: 'Set Business Category', checked: false },
    { id: 'logo', label: 'Add Logo', checked: false },
    { id: 'images', label: 'Add Images (up to 6)', checked: false },
    { id: 'theme', label: 'Set Theme Color', checked: false },
    { id: 'services', label: 'Add Categories & Services', checked: false }
  ]);

  const [supportForm, setSupportForm] = useState<SupportForm>({
    topic: '',
    steps: '',
    device: ''
  });

  const [isSendingSupport, setIsSendingSupport] = useState(false);
  const [supportSendError, setSupportSendError] = useState<string | null>(null);
  const [supportAttachments, setSupportAttachments] = useState<File[]>([]);
  const [supportAttachmentsError, setSupportAttachmentsError] = useState<string | null>(null);

  const isBusinessUser = useMemo(() => hasActiveBusinessRoles(user), [user]);

  useEffect(() => {
    const loadUnreadCount = async () => {
      if (!currentBusiness?.id) return;
      try {
        const response = await apiService.getNotifications(currentBusiness.id, { page: 1, limit: 1, isRead: false });
        const totalUnread = response.pagination?.total || 0;
        setUnreadNotifications(Array(totalUnread).fill(null) as Notification[]);
      } catch (error) {
        console.error('Error loading unread notifications count:', error);
        setUnreadNotifications([]);
      }
    };
    loadUnreadCount();
  }, [currentBusiness?.id]);

  const setupProgress = useMemo(() => {
    const completed = checklistItems.filter((i) => i.checked).length;
    const total = checklistItems.length;
    const percentage = total === 0 ? 0 : Math.round((completed / total) * 100);
    return { completed, total, percentage };
  }, [checklistItems]);

  useEffect(() => {
    const loadSetupChecklist = async () => {
      if (!showSetupChecklist) return;
      if (!currentBusiness?.id) return;

      setIsLoadingSetupChecklist(true);
      setSetupChecklistError(null);

      try {
        const status = await apiService.getSetupHealthChecklist(currentBusiness.id);
        setChecklistItems((items) =>
          items.map((i) => ({
            ...i,
            checked: Boolean((status as any)[i.id])
          }))
        );
      } catch (error) {
        console.error('Error loading setup health checklist:', error);
        setSetupChecklistError('Unable to load checklist status. Please try again.');
      } finally {
        setIsLoadingSetupChecklist(false);
      }
    };

    loadSetupChecklist();
  }, [showSetupChecklist, currentBusiness?.id]);

  const toggleQA = (id: string) => setExpandedQA((prev) => (prev === id ? null : id));

  const normalizeForSearch = (value: string): string =>
    (value || '')
      .toLowerCase()
      .replace(/[’']/g, "'")
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  // Lightweight fuzzy match:
  // - token includes match (all tokens must appear), OR
  // - subsequence match within a single word (handles partial/typo-ish input without lots of false positives)
  const fuzzyMatch = (query: string, text: string): boolean => {
    const q = normalizeForSearch(query);
    if (!q) return true;
    const t = normalizeForSearch(text);
    if (!t) return false;

    const tokens = q.split(' ').filter(Boolean);
    const allTokensIncluded = tokens.every((tok) => t.includes(tok));
    if (allTokensIncluded) return true;

    const words = t.split(' ').filter(Boolean);

    const matchesTokenInWordFuzzy = (token: string, word: string): boolean => {
      if (!token || !word) return false;
      if (word.includes(token)) return true;

      // Subsequence match within a single word, but require reasonable "density"
      // so we don't match completely unrelated text.
      const needle = token.replace(/\s/g, '');
      if (needle.length < 3) return false;

      let firstIdx = -1;
      let lastIdx = -1;
      let from = 0;
      for (const ch of needle) {
        const idx = word.indexOf(ch, from);
        if (idx === -1) return false;
        if (firstIdx === -1) firstIdx = idx;
        lastIdx = idx;
        from = idx + 1;
      }

      const span = lastIdx - firstIdx + 1;
      if (span <= 0) return false;
      const density = needle.length / span; // 1.0 means contiguous
      return density >= 0.6;
    };

    // All query tokens must match either as a substring in the full text
    // or as a fuzzy match within at least one word.
    return tokens.every((tok) => {
      if (t.includes(tok)) return true;
      return words.some((w) => matchesTokenInWordFuzzy(tok, w));
    });
  };

  const handleSupportFormChange = (field: keyof SupportForm, value: string) => {
    setSupportForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSupportSubmit = async () => {
    if (!currentBusiness?.id) return;
    setIsSendingSupport(true);
    setSupportSendError(null);
    try {
      const name = user?.firstName && user?.lastName 
        ? `${user.firstName} ${user.lastName}`.trim()
        : '';
      const email = (user?.email || '').trim();
      const businessName = currentBusiness.name || '';

      const formData = new FormData();
      formData.append('name', name);
      formData.append('email', email);
      formData.append('businessName', businessName);
      formData.append('topic', supportForm.topic || '');
      formData.append('steps', supportForm.steps || '');
      formData.append('device', supportForm.device || '');
      supportAttachments.forEach((file) => {
        formData.append('attachments', file, file.name);
      });

      await apiService.submitSupportRequest(currentBusiness.id, formData);
      setShowContactSupport(false);
    } catch (error) {
      console.error('Error submitting support request:', error);
      setSupportSendError('Failed to send to support. Please try again.');
    } finally {
      setIsSendingSupport(false);
    }
  };

  const handleNotificationsClick = () => navigate('/dashboard/business?section=notifications');

  const goBackToDashboard = () => {
    const businessId = currentBusiness?.id || '';
    const canSeeBusinessAppointments = !!businessId && isBusinessOwnerForBusiness(user, businessId);
    navigate(`/dashboard/business?section=${canSeeBusinessAppointments ? 'appointments' : 'my-appointments'}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationBar unreadNotificationsCount={unreadNotifications.length} onNotificationsClick={handleNotificationsClick} />

      {showGuidedSetup && isBusinessUser && (
        <GuidedSetupModal
          businessName={currentBusiness?.name}
          onClose={() => setShowGuidedSetup(false)}
          onOpenSiteConfigTab={(tab: SiteConfigTab) => {
            navigate(`/dashboard/business?section=site-config&siteConfigTab=${encodeURIComponent(tab)}`);
            setShowGuidedSetup(false);
          }}
          onOpenServicesManagement={() => {
            navigate('/dashboard/business?section=services');
            setShowGuidedSetup(false);
          }}
          onDone={() => navigate('/dashboard/business?section=site-config&siteConfigTab=business-info')}
          stepIndex={guidedSetupStepIndex}
          onStepIndexChange={setGuidedSetupStepIndex}
        />
      )}

      <div className="max-w-5xl mx-auto px-4 py-8 sm:py-10">
        <button
          type="button"
          onClick={goBackToDashboard}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Business Dashboard
        </button>

        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Help &amp; Support</h1>
          <p className="mt-1 text-gray-600">Quick help, guided tools, and common questions.</p>
        </div>

        <div className="flex flex-col gap-4">
          {/* 1) Guided Setup Wizard */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 p-4 sm:p-6">
            <div className="flex gap-4 items-start">
              <div className="h-11 w-11 rounded-2xl bg-gray-100 border border-gray-200/60 flex items-center justify-center shrink-0">
                <Wand2 className="h-5 w-5 text-gray-700" />
              </div>
              <div className="flex-grow min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Guided Setup Wizard</h2>
                <p className="text-sm text-gray-600">
                  Walk through the key setup steps for your business: info, service availability, hours, branding, website
                  preview, and service management.
                </p>
                {!isBusinessUser ? (
                  <p className="mt-3 text-sm text-gray-500">Sign in with a business account to access the Guided Setup.</p>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => setShowGuidedSetup(true)}
                      className="aacore-btn2-primary inline-flex items-center justify-center gap-2 px-4"
                    >
                      <Wand2 className="h-4 w-4" />
                      Open Guided Setup
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard/business?section=site-config')}
                      className="aacore-btn2-outline bg-white hover:bg-gray-50 border-gray-200 inline-flex items-center justify-center gap-2 px-4"
                    >
                      <Globe2 className="h-4 w-4" />
                      Go to Site Configuration
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 2) Setup Health Checklist */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 p-4 sm:p-6">
            <div className="flex gap-4 items-start">
              <div className="h-11 w-11 rounded-2xl bg-gray-100 border border-gray-200/60 flex items-center justify-center shrink-0">
                <ClipboardCheck className="h-5 w-5 text-gray-700" />
              </div>
              <div className="flex-grow min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Setup Health Checklist</h2>
                <p className="text-sm text-gray-600">
                  Quickly review and complete essential setup items to ensure your store is ready for customers, bookings, and
                  online visibility.
                </p>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowSetupChecklist(true)}
                    className="aacore-btn2-primary inline-flex items-center justify-center gap-2 px-4"
                  >
                    <ClipboardCheck className="h-4 w-4" />
                    Open Setup Checklist
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 3) Page Definitions */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 p-4 sm:p-6">
            <div className="flex gap-4 items-start">
              <div className="h-11 w-11 rounded-2xl bg-gray-100 border border-gray-200/60 flex items-center justify-center shrink-0">
                <BookOpen className="h-5 w-5 text-gray-700" />
              </div>
              <div className="flex-grow min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Page Definitions</h2>
                <p className="text-sm text-gray-600">Quick explanations of what each area does, so owners and staff know where to go.</p>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowPageDefinitions(true)}
                    className="aacore-btn2-primary inline-flex items-center justify-center gap-2 px-4"
                  >
                    <BookOpen className="h-4 w-4" />
                    Open Page Definitions
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 4) Q&A */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 p-4 sm:p-6">
            <div className="flex gap-4 items-start">
              <div className="h-11 w-11 rounded-2xl bg-gray-100 border border-gray-200/60 flex items-center justify-center shrink-0">
                <MessageSquare className="h-5 w-5 text-gray-700" />
              </div>
              <div className="flex-grow min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Q&amp;A</h2>
                <p className="text-sm text-gray-600">Answers to common questions about bookings, team access, services, and websites.</p>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowQA(true)}
                    className="aacore-btn2-primary inline-flex items-center justify-center gap-2 px-4"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Open Q&amp;A
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 5) Contact Support */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 p-4 sm:p-6">
            <div className="flex gap-4 items-start">
              <div className="h-11 w-11 rounded-2xl bg-gray-100 border border-gray-200/60 flex items-center justify-center shrink-0">
                <LifeBuoy className="h-5 w-5 text-gray-700" />
              </div>
              <div className="flex-grow min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Contact Support</h2>
                <p className="text-sm text-gray-600">
                  Report an issue or ask a question. Please be specific and attach screenshots so we can help quickly.
                </p>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowContactSupport(true)}
                    className="aacore-btn2-primary inline-flex items-center justify-center gap-2 px-4"
                  >
                    <LifeBuoy className="h-4 w-4" />
                    Contact Support
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Setup Health Checklist Modal */}
      {showSetupChecklist && (
        <ModalShell
          icon={<ClipboardCheck className="h-5 w-5 text-gray-700" />}
          title="Setup Health Checklist"
          description="Complete these items to ensure bookings, discovery, and your website experience work as expected."
          onClose={() => setShowSetupChecklist(false)}
          footer={
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowSetupChecklist(false)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-gray-700"
              >
                <X className="h-4 w-4" />
                Close
              </button>
            </div>
          }
        >
          {setupChecklistError ? (
            <div className="mb-4 p-3 border border-red-200 rounded-xl bg-red-50 text-sm text-red-700">{setupChecklistError}</div>
          ) : null}

          <div className="border border-gray-200 rounded-2xl bg-white p-4 mb-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="font-semibold text-gray-900">Setup progress</div>
              <div className="text-sm text-gray-600">
                {setupProgress.completed} of {setupProgress.total} completed
              </div>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div className="bg-[#0B4778] h-2 rounded-full transition-all" style={{ width: `${setupProgress.percentage}%` }} />
            </div>
          </div>

          <div className="space-y-3">
            {checklistItems.map((item) => (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                aria-label={`Open setup step: ${item.label}`}
                onClick={() => {
                  // Deep-link to the corresponding settings page/tab.
                  setShowSetupChecklist(false);
                  if (item.id === 'timezone') {
                    navigate('/dashboard/business?section=site-config&siteConfigTab=operating-hours');
                    return;
                  }
                  if (item.id === 'hours') {
                    navigate('/dashboard/business?section=site-config&siteConfigTab=operating-hours');
                    return;
                  }
                  if (item.id === 'services') {
                    navigate('/dashboard/business?section=services');
                    return;
                  }
                  if (item.id === 'bizCategory') {
                    navigate('/dashboard/business?section=site-config&siteConfigTab=services-categories');
                    return;
                  }
                  if (item.id === 'logo' || item.id === 'images' || item.id === 'theme') {
                    navigate('/dashboard/business?section=site-config&siteConfigTab=branding-theme');
                    return;
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    // Keep behavior consistent with click.
                    (e.currentTarget as HTMLDivElement).click();
                  }
                }}
                className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl bg-white cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#0B4778]/30"
              >
                <div className="flex items-start pt-0.5 pointer-events-none">
                  <input
                    type="checkbox"
                    checked={item.checked}
                    onChange={() => {}}
                    disabled={isLoadingSetupChecklist}
                    className="w-4 h-4 rounded border-gray-300 text-[#0B4778] focus:ring-[#0B4778] disabled:opacity-60"
                  />
                </div>
                <div className="flex-grow min-w-0">
                  <div className="font-semibold text-gray-900">
                    {item.id === 'images' ? (
                      <>
                        Add Images <span className="text-gray-500 font-normal">(up to 6)</span>
                      </>
                    ) : (
                      item.label
                    )}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {item.id === 'timezone' && 'Ensure bookings, notifications, and reminders use the correct local time.'}
                    {item.id === 'hours' && 'Define when your business is open and available for appointments.'}
                    {item.id === 'services' && 'Create service categories and services customers can book.'}
                    {item.id === 'bizCategory' && 'Choose a business category to improve discovery and search accuracy.'}
                    {item.id === 'logo' && 'Upload your business logo for branding across your site and booking pages.'}
                    {item.id === 'images' && 'Add photos to showcase your work, space, or services.'}
                    {item.id === 'theme' && "Customize your site's primary color to match your brand."}
                  </div>
                </div>
                <div className="shrink-0 text-gray-400 pointer-events-none">
                  <ChevronRight className="h-4 w-4" />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 p-3 border border-gray-200 rounded-xl bg-gray-50">
            <div className="flex gap-2">
              <Info className="h-4 w-4 text-gray-600 mt-0.5 shrink-0" />
              <div className="text-xs text-gray-600">
                Checklist status is loaded from your current database configuration. Each item will eventually deep-link to the correct
                settings page.
              </div>
            </div>
          </div>
        </ModalShell>
      )}

      {/* Page Definitions Modal */}
      {showPageDefinitions && (
        <ModalShell
          icon={<BookOpen className="h-5 w-5 text-gray-700" />}
          title="Page Definitions"
          description="Quick explanations of what each area does, so owners and staff know where to go."
          onClose={() => setShowPageDefinitions(false)}
          footer={
            <div className="flex justify-end">
              <button
                onClick={() => setShowPageDefinitions(false)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-gray-700"
              >
                <X className="h-4 w-4" />
                Close
              </button>
            </div>
          }
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="p-4 border border-gray-200 rounded-xl bg-white">
              <div className="font-semibold flex items-center gap-2 mb-3 text-gray-900">
                <Building2 className="h-4 w-4" /> Business Section
              </div>
              <div className="space-y-3 text-sm">
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Appointments</div>
                  <div className="text-gray-600">
                    Business-wide appointment view. Single-user businesses reflect the owner&apos;s schedule. Multi-user businesses show
                    all professionals with filtering by individual.
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Customers</div>
                  <div className="text-gray-600">
                    View customer profiles, contact details, and appointment history. New customers are added during appointment
                    creation.
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Closures</div>
                  <div className="text-gray-600">
                    Manage business or personal time off. For single-user businesses, owners typically use this to block personal
                    time off.
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">
                    Gift Cards <span className="ml-1 text-xs px-1.5 py-0.5 bg-gray-200 rounded text-gray-700">Coming soon</span>
                  </div>
                  <div className="text-gray-600">Manage and sell digital gift cards for your business.</div>
                </div>
              </div>
            </div>

            <div className="p-4 border border-gray-200 rounded-xl bg-white">
              <div className="font-semibold flex items-center gap-2 mb-3 text-gray-900">
                <Users className="h-4 w-4" /> Team Section
              </div>
              <div className="space-y-3 text-sm">
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Roles &amp; Access</div>
                  <div className="text-gray-600">Define custom roles and permissions that can later be assigned to personnel.</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Personnel</div>
                  <div className="text-gray-600">Add and manage team members. Send invitations and assign roles and service access.</div>
                </div>
              </div>
            </div>

            <div className="p-4 border border-gray-200 rounded-xl bg-white">
              <div className="font-semibold flex items-center gap-2 mb-3 text-gray-900">
                <Globe2 className="h-4 w-4" /> Site Section
              </div>
              <div className="space-y-3 text-sm">
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Testimonials</div>
                  <div className="text-gray-600">
                    Customer ratings and reviews for your business. For single-user businesses, this reflects overall business feedback.
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Site Configuration</div>
                  <div className="text-gray-600">
                    Manage your website settings, branding, and visibility. Refer to{' '}
                    <span className="px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50 text-gray-900 text-xs font-mono">
                      Open Guided Setup
                    </span>{' '}
                    for step-by-step configuration.
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border border-gray-200 rounded-xl bg-white">
              <div className="font-semibold flex items-center gap-2 mb-3 text-gray-900">
                <Briefcase className="h-4 w-4" /> My Work <span className="text-gray-500 font-normal">(2+ users only)</span>
              </div>
              <div className="space-y-3 text-sm">
                <div>
                  <div className="font-semibold text-gray-900 mb-1">My Appointments</div>
                  <div className="text-gray-600">Individual view for a service provider to manage assigned appointments.</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">My Services</div>
                  <div className="text-gray-600">
                    Services the current provider can perform. Single-user businesses typically show the full business service list.
                    Multi-user businesses allow providers to select only what they can perform.
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">My Work Schedule</div>
                  <div className="text-gray-600">
                    Set working hours and availability. Single-user businesses mirror business hours. Multi-user businesses are per provider.
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">My Work Schedule → Time Off</div>
                  <div className="text-gray-600">Block dates or times when the provider is not available for booking.</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">My Reviews</div>
                  <div className="text-gray-600">Reviews tied to the provider&apos;s completed appointments.</div>
                </div>
              </div>
            </div>
          </div>
        </ModalShell>
      )}

      {/* Q&A Modal */}
      {showQA && (
        <ModalShell
          icon={<MessageSquare className="h-5 w-5 text-gray-700" />}
          title="Q&A"
          description="Quick answers to common questions about bookings, services, staff, and your website."
          onClose={() => setShowQA(false)}
          footer={
            <div className="flex justify-end">
              <button
                onClick={() => setShowQA(false)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-gray-700"
              >
                <X className="h-4 w-4" />
                Close
              </button>
            </div>
          }
        >
          {(() => {
            const qaItems = [
              {
                id: 'q1',
                q: "Why don’t I see any available times on my booking page?",
                searchText: "available times booking page business hours work schedule timezone service duration",
                a: (
                  <>
                    Most often, it’s because business hours or your work schedule is not set, the timezone is incorrect, or the service duration doesn’t fit within available hours.
                    Check{' '}
                    <span className="px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50 text-gray-900 text-xs font-mono">Timezone</span>,{' '}
                    <span className="px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50 text-gray-900 text-xs font-mono">Business Hours</span>, and{' '}
                    <span className="px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50 text-gray-900 text-xs font-mono">My Work Schedule</span>.
                  </>
                )
              },
              {
                id: 'q2',
                q: 'How do I add or edit services and categories?',
                searchText: 'add edit services categories my services',
                a: (
                  <>
                    Go to{' '}
                    <span className="px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50 text-gray-900 text-xs font-mono">Services</span>{' '}
                    to create categories first, then add services under each category. In multi-user businesses, providers also choose which services they can perform under{' '}
                    <span className="px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50 text-gray-900 text-xs font-mono">My Services</span>.
                  </>
                )
              },
              {
                id: 'q3',
                q: "What’s the difference between Business Hours and My Work Schedule?",
                searchText: 'difference business hours my work schedule teams providers time off',
                a: (
                  <>
                    <strong>Business Hours</strong> define when the store is open. <strong>My Work Schedule</strong> defines when an individual provider is available.
                    For single-user businesses, these are usually the same. For teams, each provider sets their own schedule and time off.
                  </>
                )
              },
              {
                id: 'q4',
                q: 'How do I invite a team member to join my business?',
                searchText: 'invite team member personnel roles access my services',
                a: (
                  <>
                    Go to{' '}
                    <span className="px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50 text-gray-900 text-xs font-mono">Personnel</span>{' '}
                    and send an invitation. After they accept, assign their role in{' '}
                    <span className="px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50 text-gray-900 text-xs font-mono">Roles &amp; Access</span>{' '}
                    and allow services under{' '}
                    <span className="px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50 text-gray-900 text-xs font-mono">My Services</span>{' '}
                    (per provider).
                  </>
                )
              },
              {
                id: 'q5',
                q: 'Where do customer reviews show up?',
                searchText: 'customer reviews testimonials my reviews provider specific',
                a: (
                  <>
                    Business-wide reviews appear under{' '}
                    <span className="px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50 text-gray-900 text-xs font-mono">Testimonials</span>. Provider-specific reviews appear under{' '}
                    <span className="px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50 text-gray-900 text-xs font-mono">My Reviews</span>{' '}
                    and are tied to completed appointments.
                  </>
                )
              },
              {
                id: 'q6',
                q: 'How do Closures and Time Off affect booking availability?',
                searchText: 'closures time off booking availability block slots',
                a: (
                  <>
                    <strong>Closures</strong> block availability for the business (or for the owner in single-user mode). <strong>Time Off</strong> blocks availability for an individual provider. Both will remove those time slots from customer booking.
                  </>
                )
              },
              {
                id: 'q7',
                q: 'How do I turn appointment email notifications on or off?',
                searchText:
                  'appointment email notifications email appointment activities to owner appointments settings appointment settings save settings booked canceled rescheduled',
                a: (
                  <>
                    Go to{' '}
                    <span className="px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50 text-gray-900 text-xs font-mono">Appointments</span>{' '}
                    →{' '}
                    <span className="px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50 text-gray-900 text-xs font-mono">Settings</span>{' '}
                    →{' '}
                    <span className="px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50 text-gray-900 text-xs font-mono">Appointment Settings</span>
                    . Toggle <strong>Email appointment activities to owner</strong> On/Off, then click <strong>Save Settings</strong>. When enabled, you’ll receive email alerts when appointments are booked, canceled, or rescheduled.
                  </>
                )
              }
            ];

            const filtered = qaItems.filter((item) =>
              fuzzyMatch(qaSearch, `${item.q} ${item.searchText || ''}`)
            );

            return (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    ref={qaSearchInputRef}
                    value={qaSearch}
                    onChange={(e) => setQaSearch(e.target.value)}
                    placeholder="Search topics…"
                    className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                  />
                  {qaSearch.trim().length > 0 ? (
                    <button
                      type="button"
                      onClick={() => {
                        setQaSearch('');
                        qaSearchInputRef.current?.focus();
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                      aria-label="Clear search"
                      title="Clear"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>

                <div className="space-y-2">
                  {filtered.length === 0 ? (
                    <div className="p-4 border border-gray-200 rounded-xl bg-gray-50 text-sm text-gray-600">
                      No topics match your search.
                    </div>
                  ) : (
                    filtered.map((item) => (
                      <div key={item.id} className="border border-gray-200 rounded-xl bg-white overflow-hidden">
                        <button
                          type="button"
                          onClick={() => toggleQA(item.id)}
                          className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50"
                        >
                          <span className="font-semibold text-gray-900">{item.q}</span>
                          {expandedQA === item.id ? (
                            <ChevronUp className="h-5 w-5 text-gray-500 shrink-0" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-500 shrink-0" />
                          )}
                        </button>
                        {expandedQA === item.id ? (
                          <div className="p-4 pt-0 text-sm text-gray-600 border-t border-gray-100">{item.a}</div>
                        ) : null}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })()}
        </ModalShell>
      )}

      {/* Contact Support Modal */}
      {showContactSupport && (
        <ModalShell
          icon={<LifeBuoy className="h-5 w-5 text-gray-700" />}
          title="Contact Support"
          onClose={() => setShowContactSupport(false)}
          footer={
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowContactSupport(false)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-gray-700"
              >
                <X className="h-4 w-4" />
                Close
              </button>
              <button
                onClick={handleSupportSubmit}
                disabled={isSendingSupport || Boolean(supportAttachmentsError)}
                className="aacore-btn2-primary inline-flex items-center gap-2 px-4 py-2 disabled:opacity-60"
              >
                <Mail className="h-4 w-4" />
                {isSendingSupport ? 'Sending…' : 'Send to Support'}
              </button>
            </div>
          }
        >
          {supportSendError ? (
            <div className="mb-4 p-3 border border-red-200 rounded-xl bg-red-50 text-sm text-red-700">{supportSendError}</div>
          ) : null}
          <div className="p-3 border border-gray-200 rounded-xl bg-gray-50 mb-4">
            <div className="flex gap-2">
              <Info className="h-4 w-4 text-gray-600 mt-0.5 shrink-0" />
              <div className="text-xs text-gray-600">
                Tip: Include what you expected to happen, what actually happened, and steps to reproduce. Attach screenshots if possible.
              </div>
            </div>
          </div>

          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleSupportSubmit();
            }}
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Topic</label>
              <select
                value={supportForm.topic}
                onChange={(e) => handleSupportFormChange('topic', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
              >
                <option value="">Choose a topic...</option>
                <option>Setup &amp; Configuration</option>
                <option>Appointments &amp; Booking</option>
                <option>Services &amp; Categories</option>
                <option>Team &amp; Roles</option>
                <option>Website &amp; Branding</option>
                <option>Payments / Gift Cards (Coming Soon)</option>
                <option>Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Steps to Reproduce</label>
              <textarea
                value={supportForm.steps}
                onChange={(e) => handleSupportFormChange('steps', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                placeholder={'1) Go to...\n2) Click...\n3) Expected... / Actual...'}
              />
              <div className="mt-1 text-xs text-gray-600">
                Include the page name (example:{' '}
                <span className="px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50 text-gray-900 text-xs font-mono">Appointments</span>,{' '}
                <span className="px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50 text-gray-900 text-xs font-mono">My Work Schedule</span>) and any error messages.
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Attachments (optional)</label>
              <input
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf"
                disabled={isSendingSupport}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  const maxFiles = 5;
                  const maxSizeBytes = 50 * 1024 * 1024;
                  const allowedTypes = new Set(['image/png', 'image/jpeg', 'application/pdf']);

                  if (files.length > maxFiles) {
                    setSupportAttachments([]);
                    setSupportAttachmentsError('Maximum 5 files allowed.');
                    e.target.value = '';
                    return;
                  }

                  for (const f of files) {
                    if (!allowedTypes.has(f.type)) {
                      setSupportAttachments([]);
                      setSupportAttachmentsError('Only PNG, JPG, and PDF files are allowed.');
                      e.target.value = '';
                      return;
                    }
                    if (f.size > maxSizeBytes) {
                      setSupportAttachments([]);
                      setSupportAttachmentsError('Each file must be 50MB or less.');
                      e.target.value = '';
                      return;
                    }
                  }

                  setSupportAttachments(files);
                  setSupportAttachmentsError(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
              />
              <div className="mt-1 text-xs text-gray-600">
                Allowed types: PNG, JPG, PDF. Max 5 files. Max 50MB per file.
              </div>
              {supportAttachmentsError ? (
                <div className="mt-2 text-xs text-red-700">{supportAttachmentsError}</div>
              ) : supportAttachments.length > 0 ? (
                <div className="mt-2 text-xs text-gray-600">
                  Selected: {supportAttachments.map((f) => f.name).join(', ')}
                </div>
              ) : null}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Device / Browser (optional)</label>
              <input
                type="text"
                value={supportForm.device}
                onChange={(e) => handleSupportFormChange('device', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                placeholder="Example: iPhone Safari, Windows Chrome, Android Chrome"
              />
            </div>

            <div className="p-3 border border-gray-200 rounded-xl bg-gray-50">
              <div className="flex gap-2">
                <Mail className="h-4 w-4 text-gray-600 mt-0.5 shrink-0" />
                <div className="text-xs text-gray-600">
                  This form submits directly to support and sends details to <strong>support@aainomatic.com</strong>.
                </div>
              </div>
            </div>
          </form>
        </ModalShell>
      )}
    </div>
  );
};

export default HelpAndSupportPage;

